import { useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import { Alert, Button, Card, Col, Container, Form, Row, Spinner, Table } from 'react-bootstrap'
import { ArrowLeft, Search, Download, CheckCircle2, Circle } from 'lucide-react'
import {
  fetchDistributors,
  searchDistributorWebsite,
  searchDistributorProductVariants,
  searchDistributorCatalog,
  fetchProducts,
  fetchProductSourceOfTruthReadiness,
  confirmDistributorScrape,
  enqueueKingspanScrape,
  enqueuePendingDistributorScrapes,
  type DistributorSearchResultDto,
  type ProductDto,
  type ProductSoTReadinessDto,
  type ConfirmCreateKingspanProductRequest,
} from '../services/api'
import '../styles/pageShellCompact.css'
import './DistributorSearch.css'

type SotMode = 'existing' | 'create'
type SearchMode = 'productVariants' | 'catalog' | 'brand'

function buildProductSearchHint(p: ProductDto): string {
  const model = p.name.match(/\b(K\d+[A-Za-z]*|TP\d+[A-Za-z]*|GG\d+[A-Za-z]*)\b/i)?.[0]
  return model ?? p.code
}

function searchErrorMessage(err: unknown): string {
  const axiosErr = err as {
    response?: { data?: string | { title?: string; detail?: string; message?: string } }
    message?: string
  }
  const data = axiosErr.response?.data
  if (typeof data === 'string' && data.trim()) return data
  if (data && typeof data === 'object') {
    const detail = data.detail || data.title || data.message
    if (detail) return detail
  }
  return axiosErr.message || 'Search failed'
}

function ReadinessChecklist({ readiness }: { readiness: ProductSoTReadinessDto | undefined }) {
  if (!readiness) return <div className="text-muted small">Select a Kingspan product to see readiness.</div>
  const hasApprovedFacts = readiness.kingspanFactCount > 0
  const items = [
    { ok: !readiness.isPlaceholder, label: 'Not a distributor placeholder product' },
    { ok: readiness.hasKingspanUrl || hasApprovedFacts, label: 'Kingspan URL or approved MIM PIM facts' },
    { ok: readiness.hasFamily, label: 'Product family set' },
    { ok: hasApprovedFacts, label: `Approved source-of-truth facts (${readiness.kingspanFactCount})` },
  ]
  return (
    <ul className="list-unstyled small mb-0">
      {items.map(item => (
        <li key={item.label} className="d-flex align-items-start gap-2 mb-1">
          {item.ok ? (
            <CheckCircle2 size={16} className="text-success flex-shrink-0 mt-1" aria-hidden />
          ) : (
            <Circle size={16} className="text-muted flex-shrink-0 mt-1" aria-hidden />
          )}
          <span>{item.label}</span>
        </li>
      ))}
    </ul>
  )
}

export default function DistributorSearch() {
  const { distributorId } = useParams<{ distributorId: string }>()
  const navigate = useNavigate()

  const [selectedUrls, setSelectedUrls] = useState<Set<string>>(new Set())
  const [searchResults, setSearchResults] = useState<DistributorSearchResultDto[] | null>(null)
  const [searchMessage, setSearchMessage] = useState<string | null>(null)
  const [, setSearchDiagnostics] = useState<{
    brandKeywordCount: number
    firecrawlWebHits: number
    mapHitsConsidered: number
    skippedExistingTargets: number
  } | null>(null)
  const [searchError, setSearchError] = useState<string | null>(null)
  const [searchMode, setSearchMode] = useState<SearchMode>('productVariants')
  const [productQuery, setProductQuery] = useState('')

  const [sotMode, setSotMode] = useState<SotMode>('existing')
  const [productSearch, setProductSearch] = useState('')
  const [selectedProductId, setSelectedProductId] = useState<string>('')
  const [createForm, setCreateForm] = useState<ConfirmCreateKingspanProductRequest>({
    code: '',
    name: '',
    kingspanUrl: '',
    family: '',
    country: '',
    division: '',
    market: '',
  })

  const { data: distributors = [] } = useQuery({
    queryKey: ['distributors'],
    queryFn: fetchDistributors,
  })
  const distributor = distributors.find(d => d.id === distributorId)

  const { data: productSearchResults = [], isFetching: productSearchLoading } = useQuery({
    queryKey: ['products-sot-search', productSearch],
    queryFn: () => fetchProducts({ search: productSearch.trim() }),
    enabled: sotMode === 'existing' && productSearch.trim().length >= 2,
  })

  const effectiveProductId = sotMode === 'existing' ? selectedProductId : ''

  const {
    data: readiness,
    refetch: refetchReadiness,
    isFetching: readinessLoading,
  } = useQuery({
    queryKey: ['product-sot-readiness', effectiveProductId],
    queryFn: () => fetchProductSourceOfTruthReadiness(effectiveProductId),
    enabled: !!effectiveProductId,
  })

  const searchMutation = useMutation({
    mutationFn: () => {
      if (searchMode === 'productVariants') {
        if (!selectedProductId) {
          throw new Error('Select a Kingspan source-of-truth product on the right before searching.')
        }
        return searchDistributorProductVariants(
          distributorId!,
          selectedProductId,
          productQuery.trim() || undefined,
        )
      }
      if (searchMode === 'catalog') return searchDistributorCatalog(distributorId!)
      return searchDistributorWebsite(distributorId!)
    },
    onSuccess: (data) => {
      setSearchResults(data.results)
      setSearchMessage(data.message ?? null)
      setSearchDiagnostics({
        brandKeywordCount: data.brandKeywordCount,
        firecrawlWebHits: data.firecrawlWebHits,
        mapHitsConsidered: data.mapHitsConsidered,
        skippedExistingTargets: data.skippedExistingTargets,
      })
      setSearchError(null)
      setSelectedUrls(new Set(data.results.map(r => r.url)))
    },
    onError: (err: unknown) => {
      setSearchError(searchErrorMessage(err))
      setSearchResults(null)
      setSearchMessage(null)
      setSearchDiagnostics(null)
    },
  })

  const kingspanScrapeMutation = useMutation({
    mutationFn: (productId: string) => enqueueKingspanScrape(productId),
    onSuccess: () => {
      void refetchReadiness()
    },
  })

  const pendingScrapeMutation = useMutation({
    mutationFn: (productId: string) => enqueuePendingDistributorScrapes(productId),
  })

  const confirmMutation = useMutation({
    mutationFn: () => {
      const urls = Array.from(selectedUrls)
      const urlTitles = searchResults?.reduce<Record<string, string>>((acc, r) => {
        if (r.title) acc[r.url] = r.title
        return acc
      }, {}) ?? undefined

      if (sotMode === 'existing') {
        if (!selectedProductId) throw new Error('Select a Kingspan source-of-truth product.')
        return confirmDistributorScrape(distributorId!, {
          urls,
          kingspanProductId: selectedProductId,
          catalogOnly: searchMode === 'catalog',
          resolveVariants: searchMode === 'productVariants',
          urlTitles,
        })
      }
      if (!createForm.code.trim() || !createForm.name.trim() || !createForm.kingspanUrl.trim() || !createForm.family.trim()) {
        throw new Error('Code, name, Kingspan URL, and family are required to create a product.')
      }
      return confirmDistributorScrape(distributorId!, {
        urls,
        catalogOnly: searchMode === 'catalog',
        resolveVariants: searchMode === 'productVariants',
        urlTitles,
        createKingspan: {
          code: createForm.code.trim(),
          name: createForm.name.trim(),
          kingspanUrl: createForm.kingspanUrl.trim(),
          family: createForm.family.trim(),
          country: createForm.country?.trim() || undefined,
          division: createForm.division?.trim() || undefined,
          market: createForm.market?.trim() || undefined,
        },
      })
    },
  })

  const canConfirm = useMemo(() => {
    if (selectedUrls.size === 0) return false
    if (sotMode === 'existing') return !!selectedProductId
    return !!(createForm.code.trim() && createForm.name.trim() && createForm.kingspanUrl.trim() && createForm.family.trim())
  }, [selectedUrls, sotMode, selectedProductId, createForm])

  useEffect(() => {
    if (confirmMutation.isSuccess && confirmMutation.data?.kingspanProductId && !confirmMutation.data.soTReady) {
      void refetchReadiness()
    }
  }, [confirmMutation.isSuccess, confirmMutation.data, refetchReadiness])

  const toggleUrl = (url: string) => {
    setSelectedUrls(prev => {
      const next = new Set(prev)
      if (next.has(url)) next.delete(url)
      else next.add(url)
      return next
    })
  }

  const toggleAll = () => {
    if (!searchResults) return
    if (selectedUrls.size === searchResults.length) {
      setSelectedUrls(new Set())
    } else {
      setSelectedUrls(new Set(searchResults.map(r => r.url)))
    }
  }

  const pickProduct = (p: ProductDto) => {
    setSelectedProductId(p.id)
    setProductSearch(`${p.code} — ${p.name}`)
    if (searchMode === 'productVariants') {
      setProductQuery(buildProductSearchHint(p))
    }
  }

  const search1Ready = searchMode !== 'productVariants' || (sotMode === 'existing' && !!selectedProductId)

  const lastConfirmProductId = confirmMutation.data?.kingspanProductId

  return (
    <Container fluid className="distributor-search-page px-3 px-md-4 page-shell-compact py-3">
      <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
        <Button variant="outline-secondary" size="sm" onClick={() => navigate('/distributors')}>
          <ArrowLeft size={14} /> Back
        </Button>
        <h1 className="page-shell-title mb-0">Discover Product Pages</h1>
      </div>

      {distributor ? (
        <Alert variant="info" className="mb-2 py-2">
          <div className="fw-semibold mb-1">SoT-first workflow</div>
          <div>
            Select distributor pages, then assign the <strong>Kingspan source-of-truth product</strong> to compare against.
            Distributor scrape runs only when Kingspan URL, family, and attribute values are ready.
          </div>
          <div className="mt-1 text-muted small">
            Searching <strong>{distributor.name}</strong> ({distributor.websiteUrl ?? distributor.domain}).
            Brand keywords: <Link to="/pim?tab=brand-whitelist">MIM Setup → Brand whitelist</Link>.
          </div>
        </Alert>
      ) : (
        <Alert variant="warning" className="py-2 mb-2">Distributor not found.</Alert>
      )}

      <Card className="border shadow-sm mb-2">
        <Card.Body>
          <Form.Group className="mb-2">
            <Form.Label className="small fw-semibold mb-1">Search mode</Form.Label>
            <Form.Select
              size="sm"
              value={searchMode}
              onChange={e => setSearchMode(e.target.value as SearchMode)}
              style={{ maxWidth: 420 }}
            >
              <option value="productVariants">Search 1 — exact product + all size variants</option>
              <option value="catalog">Search 2 — brand catalog (no scrape until promoted)</option>
              <option value="brand">Legacy — brand keyword web search</option>
            </Form.Select>
          </Form.Group>
          {searchMode === 'productVariants' && (
            <>
              {!selectedProductId && (
                <Alert variant="warning" className="py-2 mb-2 small">
                  Search 1 matches distributor pages to a <strong>Kingspan source-of-truth product</strong>.
                  Select the product in the panel on the right, then search.
                </Alert>
              )}
              {selectedProductId && productSearch && (
                <div className="small text-muted mb-2">
                  Kingspan product: <strong>{productSearch}</strong>
                </div>
              )}
              <Form.Group className="mb-2">
                <Form.Label className="small mb-0">Optional search hint</Form.Label>
                <Form.Control
                  size="sm"
                  value={productQuery}
                  onChange={e => setProductQuery(e.target.value)}
                  placeholder="Auto-filled from product code/name (e.g. K5)"
                  style={{ maxWidth: 420 }}
                  disabled={!selectedProductId}
                />
                <Form.Text className="text-muted">
                  The search uses the Kingspan product code and name; adjust the hint only if the distributor site uses different wording.
                </Form.Text>
              </Form.Group>
            </>
          )}
          <div className="d-flex flex-wrap gap-2 align-items-center">
            <Button
              variant="primary"
              size="sm"
              disabled={!distributorId || searchMutation.isPending || !search1Ready}
              onClick={() => searchMutation.mutate()}
            >
              {searchMutation.isPending ? (
                <>
                  <Spinner animation="border" size="sm" className="me-1" />
                  Searching...
                </>
              ) : (
                <>
                  <Search size={14} className="me-1" />
                  Search distributor website
                </>
              )}
            </Button>
            {searchMutation.isPending && (
              <span className="text-muted small">
                {searchMode === 'productVariants'
                  ? 'Searching distributor site for pages matching the Kingspan product (15–30 seconds)...'
                  : 'Querying Firecrawl (15–30 seconds)...'}
              </span>
            )}
          </div>
        </Card.Body>
      </Card>

      {searchError && <Alert variant="danger" className="py-2 mb-2">{searchError}</Alert>}

      {searchResults !== null && (
        <Row className="g-2">
          <Col lg={7}>
            <Card className="distributor-search-results-card border shadow-sm h-100">
              <Card.Body>
                {searchMessage && searchResults.length > 0 && (
                  <Alert variant="info" className="py-2 mb-2">{searchMessage}</Alert>
                )}
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                  <div className="results-toolbar-title">
                    Search results ({searchResults.length})
                  </div>
                  {searchResults.length > 0 && (
                    <Button variant="outline-secondary" size="sm" onClick={toggleAll}>
                      {selectedUrls.size === searchResults.length ? 'Deselect all' : 'Select all'}
                    </Button>
                  )}
                </div>

                {searchResults.length === 0 ? (
                  <Alert variant="secondary" className="mb-0 py-2 small">
                    {searchMessage ?? 'No product pages found.'}
                  </Alert>
                ) : (
                  <div className="distributor-search-table-wrap">
                    <Table striped hover className="mb-0 align-middle" size="sm">
                      <thead>
                        <tr>
                          <th style={{ width: 40 }}>
                            <Form.Check
                              checked={selectedUrls.size === searchResults.length}
                              onChange={toggleAll}
                            />
                          </th>
                          <th style={{ width: 44 }}>#</th>
                          <th>Page</th>
                          <th>URL</th>
                        </tr>
                      </thead>
                      <tbody>
                        {searchResults.map(r => (
                          <tr key={r.url} style={{ cursor: 'pointer' }} onClick={() => toggleUrl(r.url)}>
                            <td onClick={e => e.stopPropagation()}>
                              <Form.Check
                                checked={selectedUrls.has(r.url)}
                                onChange={() => toggleUrl(r.url)}
                              />
                            </td>
                            <td className="text-muted">{r.position}</td>
                            <td>
                              <div className="distributor-search-result-title">{r.title || '(No title)'}</div>
                              {r.description && (
                                <div className="distributor-search-result-desc">{r.description}</div>
                              )}
                            </td>
                            <td>
                              <a
                                href={r.url}
                                target="_blank"
                                rel="noreferrer"
                                className="distributor-search-result-url"
                                onClick={e => e.stopPropagation()}
                              >
                                {r.url.length > 60 ? `${r.url.substring(0, 60)}...` : r.url}
                              </a>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col lg={5}>
            <Card className="border shadow-sm h-100">
              <Card.Body className="d-flex flex-column gap-3">
                <div>
                  <div className="fw-semibold mb-2">Kingspan source of truth</div>
                  <Form.Check
                    type="radio"
                    name="sot-mode"
                    id="sot-existing"
                    label="Link existing product"
                    checked={sotMode === 'existing'}
                    onChange={() => setSotMode('existing')}
                  />
                  <Form.Check
                    type="radio"
                    name="sot-mode"
                    id="sot-create"
                    label="Create new Kingspan product"
                    checked={sotMode === 'create'}
                    onChange={() => setSotMode('create')}
                  />
                </div>

                {sotMode === 'existing' && (
                  <div>
                    <Form.Label className="small mb-1">Search product library</Form.Label>
                    <Form.Control
                      size="sm"
                      value={productSearch}
                      onChange={e => setProductSearch(e.target.value)}
                      placeholder="Type code or name (min 2 characters)"
                    />
                    {productSearchLoading && (
                      <div className="small text-muted mt-1">Searching...</div>
                    )}
                    {productSearchResults.length > 0 && (
                      <div className="border rounded mt-2" style={{ maxHeight: 160, overflow: 'auto' }}>
                        {productSearchResults.slice(0, 12).map(p => (
                          <button
                            key={p.id}
                            type="button"
                            className={`dropdown-item text-start small py-2 ${selectedProductId === p.id ? 'active' : ''}`}
                            onClick={() => pickProduct(p)}
                          >
                            <strong>{p.code}</strong> — {p.name}
                            {p.family && <span className="text-muted"> · {p.family}</span>}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {sotMode === 'create' && (
                  <Row className="g-2">
                    <Col xs={12}>
                      <Form.Label className="small mb-0">Code</Form.Label>
                      <Form.Control
                        size="sm"
                        value={createForm.code}
                        onChange={e => setCreateForm(f => ({ ...f, code: e.target.value }))}
                      />
                    </Col>
                    <Col xs={12}>
                      <Form.Label className="small mb-0">Name</Form.Label>
                      <Form.Control
                        size="sm"
                        value={createForm.name}
                        onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))}
                      />
                    </Col>
                    <Col xs={12}>
                      <Form.Label className="small mb-0">Kingspan URL</Form.Label>
                      <Form.Control
                        size="sm"
                        type="url"
                        value={createForm.kingspanUrl}
                        onChange={e => setCreateForm(f => ({ ...f, kingspanUrl: e.target.value }))}
                      />
                    </Col>
                    <Col xs={12}>
                      <Form.Label className="small mb-0">Family</Form.Label>
                      <Form.Control
                        size="sm"
                        value={createForm.family}
                        onChange={e => setCreateForm(f => ({ ...f, family: e.target.value }))}
                        placeholder="e.g. wall-insulation-boards"
                      />
                    </Col>
                  </Row>
                )}

                <div>
                  <div className="fw-semibold small mb-2">Readiness checklist</div>
                  {readinessLoading && sotMode === 'existing' && selectedProductId && (
                    <Spinner animation="border" size="sm" className="me-2" />
                  )}
                  <ReadinessChecklist readiness={sotMode === 'existing' ? readiness : undefined} />
                  {sotMode === 'create' && (
                    <div className="small text-muted mt-2">
                      A Kingspan scrape will be queued after you confirm. Distributor scrape runs once readiness is green.
                    </div>
                  )}
                </div>

                {sotMode === 'existing' && selectedProductId && readiness && !readiness.isReady && (
                  <div className="d-flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant="outline-primary"
                      disabled={kingspanScrapeMutation.isPending}
                      onClick={() => kingspanScrapeMutation.mutate(selectedProductId)}
                    >
                      {kingspanScrapeMutation.isPending ? 'Queueing…' : 'Run Kingspan scrape'}
                    </Button>
                    <Button size="sm" variant="outline-secondary" onClick={() => void refetchReadiness()}>
                      Refresh checklist
                    </Button>
                  </div>
                )}

                <Button
                  variant="success"
                  disabled={!canConfirm || confirmMutation.isPending}
                  onClick={() => confirmMutation.mutate()}
                >
                  {confirmMutation.isPending ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-1" />
                      Attaching…
                    </>
                  ) : (
                    <>
                      <Download size={14} className="me-1" />
                      Attach URLs &amp; scrape ({selectedUrls.size})
                    </>
                  )}
                </Button>

                {confirmMutation.isSuccess && confirmMutation.data && (
                  <Alert variant={confirmMutation.data.soTReady ? 'success' : 'warning'} className="py-2 mb-0 small">
                    {confirmMutation.data.message}
                    {!confirmMutation.data.soTReady && lastConfirmProductId && (
                      <div className="mt-2 d-flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          variant="outline-primary"
                          as={Link as any}
                          to={`/products/${lastConfirmProductId}`}
                        >
                          Open product to complete SoT
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-success"
                          disabled={pendingScrapeMutation.isPending}
                          onClick={() => pendingScrapeMutation.mutate(lastConfirmProductId)}
                        >
                          Queue pending distributor scrapes
                        </Button>
                      </div>
                    )}
                    {confirmMutation.data.soTReady && lastConfirmProductId && (
                      <div className="mt-2">
                        <Link to={`/products/${lastConfirmProductId}/compare`}>Open comparison</Link>
                        {' · '}
                        <Link to="/check-runs">Jobs</Link>
                      </div>
                    )}
                  </Alert>
                )}
                {confirmMutation.isError && (
                  <Alert variant="danger" className="py-2 mb-0 small">
                    {(confirmMutation.error as Error)?.message || 'Failed to attach distributor URLs.'}
                  </Alert>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
    </Container>
  )
}
